import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useCallback, useEffect, useState } from 'react';
import smartZoneIcon from '../../assets/icons/smartZoneIcon.svg';
import WhiteboardController from '../../controllers/WhiteboardController';
import {
  isSmartZone,
  type SmartZoneConfig,
  type SmartZoneObject,
} from '../../objects/SmartZoneBehaviors';
import {
  applyUpdatesForSmartZone,
  exampleSmartZone,
} from '../../services/smartZoneService';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import { svgToIcon } from '../../utils/svg';
import useWhiteboardObjects from '../hooks/useWhiteboardObjects';
import styles from './smart-zones.module.css';
import { SmartZoneEditor } from './SmartZoneEditor';
import useSelectedObject from '../hooks/useSelectedObject';
import { type SidePanelItem } from '../../shared-types';
import { zoomAndPanTo } from '../../utils/zoom';
import { toggleSmartZone } from '../../tools/smartZoneTools';

const addSmartZone = (controller: WhiteboardController) => {
  const currentZones = controller.canvas.getObjects().filter(isSmartZone);
  const newZone = exampleSmartZone(currentZones);
  const obj = controller.actionPlaceObjects(newZone, {
    select: true,
    maxZoom: 1,
  });
  editSmartZone(controller, obj as SmartZoneObject, { zoom: true });
};

interface SmartZoneListProps {
  smartZones: SmartZoneObject[];
  selectZone: (zone: SmartZoneObject) => void;
}
const SmartZoneList = ({ smartZones, selectZone }: SmartZoneListProps) => {
  const controller = useController(WhiteboardController);
  const sorted = smartZones.toSorted((a, b) =>
    a.smartZoneTitle().localeCompare(b.smartZoneTitle())
  );
  return (
    <div className={styles.smartZoneList}>
      <div className={styles.smartZoneTitleContainer}>
        <h4 className={styles.smartZoneTitle}>Choose a smart zone to edit</h4>
      </div>

      {sorted.map(smartZone => (
        <a
          key={smartZone.id}
          className={styles.smartZoneItem}
          onClick={() => {
            selectZone(smartZone);
          }}
        >
          {smartZone.smartZoneTitle()}
        </a>
      ))}
      <div className={styles.addZoneWrapper}>
        <aha-button kind='link' onClick={() => addSmartZone(controller)}>
          <i className='fa-light fa-circle-plus' /> Add another smart zone
        </aha-button>
      </div>
    </div>
  );
};

interface EditableProps {
  value: string;
  onChange: (v: string) => void;
}
const Editable: React.FC<EditableProps> = ({ value, onChange }) => {
  const valueRef = React.useRef(value);

  return (
    <div
      contentEditable='true'
      suppressContentEditableWarning={true}
      onInput={e => {
        onChange(e.currentTarget.textContent ?? '');
      }}
    >
      {valueRef.current}
    </div>
  );
};

interface SmartZoneDetailProps {
  currentSmartZone: SmartZoneObject;
}

const SmartZoneDetail: React.FC<SmartZoneDetailProps> = ApplicationView(
  ({ currentSmartZone }) => {
    const controller = useController(WhiteboardController);
    const { smartZoneFormData } = controller.props;
    const [config, setConfig] = useState<SmartZoneConfig>(
      currentSmartZone.smartZoneConfig ?? defaultConfig
    );
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
      // deselect the smart zone when exiting its detail view. In React prod mode,
      // straight instance equality fails for some reason, so compare ids instead
      return () => {
        if (controller.canvas.getActiveObject()?.id === currentSmartZone?.id) {
          controller.canvas.discardActiveObject();
          controller.canvas.requestRenderAll();
        }
      };
    }, [controller, currentSmartZone]);

    useSelectedObject({
      predicate: isSmartZone,
      onChange: obj => {
        if (!obj) {
          // if the object gets deselected, return to the list view
          controller.sidePanelController?.actionReplaceSidePanelStack([]);
        } else if (obj.id !== currentSmartZone.id) {
          // if another object is selected, switch to it
          editSmartZone(controller, obj, { zoom: false });
        }
      },
    });

    const updateConfig = useCallback(
      (updater: (current: SmartZoneConfig) => SmartZoneConfig) => {
        const updated = updater(
          currentSmartZone.smartZoneConfig ?? defaultConfig
        );
        setConfig(updated);
        controller.undoManager.withoutUndo(() => {
          currentSmartZone.set('smartZoneConfig', updated);
        });
      },
      [controller, currentSmartZone]
    );

    return (
      <>
        <div className={styles.smartZoneTitleContainer}>
          <h4 className={styles.smartZoneTitle}>
            <Editable
              onChange={content => currentSmartZone.set('textContent', content)}
              value={currentSmartZone.smartZoneTitle()}
            />
            <i className='fa-regular fa-pen color-secondary' />
          </h4>
          <aha-button
            disabled={customElementBoolAttr(isUpdating)}
            kind='ghost'
            onClick={() => {
              setIsUpdating(true);
              void applyUpdatesForSmartZone(
                controller,
                currentSmartZone
              ).finally(() => setIsUpdating(false));
            }}
          >
            <i
              className={`fa-light fa-arrows-rotate ${
                isUpdating ? styles.rotate : ''
              }`}
            ></i>{' '}
            Refresh
          </aha-button>
        </div>
        <SmartZoneEditor
          key={currentSmartZone.id}
          smartZoneFormData={smartZoneFormData ?? []}
          config={config ?? defaultConfig}
          updateConfig={updateConfig}
        />
      </>
    );
  }
);

const defaultConfig: SmartZoneConfig = {
  enabled: false,
  configurations: {},
};

const SmartZoneContent: React.FC = ApplicationView(() => {
  const controller = useController(WhiteboardController);

  const allSmartZones = useWhiteboardObjects(isSmartZone);

  useSelectedObject({
    predicate: isSmartZone,
    onChange: obj => {
      if (obj) {
        editSmartZone(controller, obj, { zoom: false });
      }
    },
  });

  if (allSmartZones.length > 0) {
    return (
      <SmartZoneList
        smartZones={allSmartZones}
        selectZone={zone => {
          editSmartZone(controller, zone);
        }}
      />
    );
  }

  return (
    <div className={styles.empty}>
      <p>Add a new smart zone to begin.</p>
      <aha-button kind='link' onClick={() => addSmartZone(controller)}>
        <i className='fa-light fa-circle-plus' /> Add a smart zone
      </aha-button>
    </div>
  );
});

const SmartZoneHeader: React.FC = () => (
  <div className={styles.smartZonesHeader}>
    Choose the attributes that will be applied to the record shapes when placed
    in the smart zone.
  </div>
);

const SmartZoneTabContent: React.FC = () => {
  const allSmartZones = useWhiteboardObjects(isSmartZone);

  if (!allSmartZones.length) {
    return null;
  }

  return <aha-sidepanel-item>{svgToIcon(smartZoneIcon)}</aha-sidepanel-item>;
};

const SmartZoneTab = ApplicationView(SmartZoneTabContent);

const icon = svgToIcon(smartZoneIcon, { width: 18 });

export const smartZoneItem: SidePanelItem = {
  id: 'smartZone',
  name: 'Smart zones',
  icon,
  alwaysRendered: false,
  tabContent: <SmartZoneTab />,
  content: <SmartZoneContent />,
  headerContent: <SmartZoneHeader />,
};

const makeEditItem = (smartZone: SmartZoneObject): SidePanelItem => ({
  id: 'editSmartZone',
  name: 'Edit smart zone',
  icon,
  alwaysRendered: false,
  content: <SmartZoneDetail currentSmartZone={smartZone} key={smartZone.id} />,
  footerContent: (
    <div className={styles.smartZoneFooterContainer}>
      <aha-button
        kind='secondary'
        onClick={() => {
          toggleSmartZone(smartZone, false);
        }}
      >
        Disable smart zone
      </aha-button>
    </div>
  ),
});

const editSmartZone = (
  controller: WhiteboardController,
  smartZone: SmartZoneObject,
  { zoom = true } = {}
) => {
  controller.sidePanelController?.actionReplaceSidePanelStack([
    makeEditItem(smartZone),
  ]);
  if (zoom) {
    controller.canvas.setActiveObject(smartZone);
    zoomAndPanTo(controller, smartZone, { maxZoom: 1 });
  }
};
